import { ArtworkImage } from './ArtworkImage';
import React, { useRef, useState } from 'react';
import { Investigator, CommonItem, UniqueItem, Spell, SkillBreakdown, UserAction, SpecialCard } from '../api';
import StatBreakdown from './StatBreakdown';
import { MonstersDetails } from './Monsters';
import { StatisticsCard } from './StatisticsCard';
import { Card, getItemImagePath, normalizeCardUri } from './Card';
import type { ItemKind, StackedItem, StackedItemWithKind } from './Card';
import { StackedItemCard } from './StackedItemCard';
import { getStatColor } from '../utils/statColor';
import { GateMarker } from './GateMarker';
import { SimpleMarkdown } from './SimpleMarkdown';
import { BastToken } from './BastToken';
import { DarkManDebtBadge } from './DarkManDebtBadge';
import { PersonalStoryCard } from './PersonalStory';
import { SpellInvestigatorContext } from './SpellCastingContext';

export interface InvestigatorSheetProps {
  investigator: Investigator;
  availableActions?: UserAction[];
  onExecuteAction?: (actionId: string) => void;
  currentLocationName?: string;
  showStats?: boolean;
  variant?: 'reference' | 'game';
  deckInfo?: {
    commonItems?: { bottomCard?: any | null };
    uniqueItems?: { bottomCard?: any | null };
    spells?: { bottomCard?: any | null };
  };
  language?: string;
}

const isUsedCard = (item: Partial<CommonItem | UniqueItem | Spell>) => !!(item.used || item.exhausted);

const getModifierImage = (modSource: string | undefined, sourceType: string | undefined | null): string | undefined => {
  if (!sourceType || sourceType === 'Mythos') return undefined;
  if (!modSource) return undefined;

  switch (sourceType) {
    case 'CommonItem':
      return getItemImagePath('common', modSource);
    case 'UniqueItem':
      return getItemImagePath('unique', modSource);
    case 'Spell':
      return getItemImagePath('spell', modSource);
    case 'Skill':
      return getItemImagePath('skill', modSource);
    case 'Ally':
      return getItemImagePath('ally', modSource);
    case 'Special':
      return getItemImagePath('special', modSource, (modSource as unknown as SpecialCard).isNodensBlessing || false);
    case 'AncientOne':
      return getItemImagePath('ancientOne', modSource);
    case 'Investigator':
      return `/Investigators/${encodeURIComponent(normalizeCardUri(modSource))}.jpg`;
    default:
      return undefined;
  }
};

export const StackedItems: React.FC<{
  title: string;
  items: StackedItem[];
  kind: ItemKind;
  entries?: StackedItemWithKind[];
  additionalUnusedItems?: React.ReactNode;
  investigatorName?: string;
}> = ({
  title,
  items,
  kind,
  entries,
  additionalUnusedItems,
  investigatorName,
}) => {
  const allEntries = entries ?? items.map(item => ({ item, kind }));
  if (allEntries.length === 0 && React.Children.count(additionalUnusedItems) === 0) return null;

  const used = allEntries.filter(entry => isUsedCard(entry.item));
  const unused = allEntries.filter(entry => !isUsedCard(entry.item));

  return (
    <SpellInvestigatorContext.Provider value={investigatorName}><div className="stacked-group">
      <div className="stacked-title">{title}</div>
      <div className="stacked-column">
        {(unused.length > 0 || React.Children.count(additionalUnusedItems) > 0) && <div className="stacked-row unused">
          {unused.map((entry, idx) => {
            return (
              <StackedItemCard
                key={`${title}-${idx}-${entry.item.name}`}
                entry={entry}
                zIndex={idx}
              />
            );
          })}
          {additionalUnusedItems}
        </div>}

        {used.length > 0 && (
          <div className="stacked-row used">
            {used.map((entry, idx) => {
              return (
                <StackedItemCard
                  key={`${title}-used-${idx}-${entry.item.name}`}
                  entry={entry}
                  zIndex={idx}
                  isUsed
                />
              );
            })}
          </div>
        )}
      </div>
    </div></SpellInvestigatorContext.Provider>
  );
};

  const StatTooltip = ({ type, styles, setOpenStatTooltip, breakdown }: { breakdown: SkillBreakdown | null, type: string, styles: any, setOpenStatTooltip: any}) => {
    if (!breakdown) {
      return null;
    }
    return (
      <div
        className="skill-tooltip"
        style={styles}
      >
        <div className="skill-tooltip-header">
          <span>{type}</span>
          <button
            type="button"
            className="skill-tooltip-close"
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpenStatTooltip(null); }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <StatBreakdown breakdown={breakdown} getModifierImage={getModifierImage} />
      </div>
    )
  }

const buildHearts = (current: number, max: number, extra: number, className: 'heart' | 'brain') => {
  const elements = [];

  if (!!extra) {
    for (let i = 0; i < extra; i++) {
      const isFilled = true;
      elements.push(
        <span key={`${className}-${i}-${isFilled}-extra`} className={`extra ${className}${isFilled ? '' : ' empty'}`} />
      );
    }
  }

  for (let i = 0; i < max; i++) {
    const isFilled = i < current;
    elements.push(
      <span key={`${className}-${i}-${isFilled}`} className={`${className}${isFilled ? '' : ' empty'}`} />
    );
  }

  return <div className="stats">{elements}</div>;
};

type TooltipPosition = {
  key: string;
  data: SkillBreakdown | null;
  top?: number;
  left: number;
  bottom?: number
};

export const InvestigatorSheet: React.FC<InvestigatorSheetProps> = ({
  investigator,
  showStats = false,
  variant = 'game',
  availableActions,
  onExecuteAction,
  currentLocationName,
  deckInfo,
  language
}) => {
  const [openStatTooltip, setOpenStatTooltip] = useState<TooltipPosition | null>(null);
  const [openBottomCardTooltip, setOpenBottomCardTooltip] = useState<boolean>(false);
  const [showStatisticsPopup, setShowStatisticsPopup] = useState<boolean>(false);
  const sliderOverlayRef = useRef<HTMLDivElement>(null);
  const statIconsRef = useRef<HTMLDivElement>(null);
  const sliderImage = `/slider.png`;
  const setActions = (availableActions || []).filter(a => a.id.startsWith('set_')).filter(a => a.investigatorName === investigator.name);
  const isDelayed = !!investigator.delayed;
  const isExplored = !!investigator.explored;
  const isArrested = currentLocationName === 'Jail Cell';
  const statusChips = [
    isDelayed ? 'Delayed' : null,
    isArrested ? 'Arrested' : null,
    investigator.isDevoured ? (investigator.requiresReplacementChoiceOnUpkeep ? 'Retired' : 'Devoured') : null,
    isExplored ? 'Explored' : null,
  ].filter(Boolean) as string[];

  const toggleStatTooltip = (key: string, data: SkillBreakdown, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (openStatTooltip?.key === key) {
      setOpenStatTooltip(null);
      return;
    }

    const overlayRect = statIconsRef.current?.getBoundingClientRect();
    const btnRect = event.currentTarget.getBoundingClientRect();

    if (overlayRect && data) {
      setOpenStatTooltip({
        key,
        data,
        left: btnRect.right - overlayRect.left,
        top: btnRect.top - overlayRect.top,
      });
    } else {
      setOpenStatTooltip({ key, left: 0, top: 0, data: null, });
    }
  };


  return (
    <div className="investigator-sheet" onClick={() => showStatisticsPopup && setShowStatisticsPopup(false)}>
      <div className="investigator-main">
        <div className="investigator-portrait">
          <InvestigatorImage investigator={investigator} />
          <div className="investigator-overlay top-left">
            <div className="clue-money">
              {investigator.clues > 0 && <div className="clue">
                {investigator.clues <= 5 ? (
                  <div className="clue-stack">
                    {Array.from({ length: investigator.clues }).map((_, idx) => (
                      <ArtworkImage loading="lazy" key={idx} src="/Clue.jpg" alt="Clue" className="clue-token" />
                    ))}
                  </div>
                ) : (
                  <>
                    <ArtworkImage loading="lazy" src="/Clue.jpg" alt="Clue" style={{borderRadius:'50%'}} />
                    <span>{investigator.clues}</span>
                  </>
                )}
              </div>}
              {investigator.money > 0 && <div className="money">
                <ArtworkImage loading="lazy" src="/money.jpg" alt="Money" />
                <span>{investigator.money}</span>
              </div>}
              {!!investigator.hasBastToken && (
                <BastToken />
              )}
              {!!investigator.eihortBroodTokens && (
                <div className='investigator-eihort-token'>
                  {investigator.eihortBroodTokens > 1 && <div className='investigator-eihort-token-count'>{investigator.eihortBroodTokens}</div>}
                  <ArtworkImage alt='Eihort token' src='/AncientOnes/EihortToken.jpg' />
                </div>
              )}
            </div>
          </div>
          {(statusChips.length > 0 || (variant !== 'reference' && investigator.hasDarkManDebt)) && (
            <div className={`investigator-status-chips${variant !== 'reference' && investigator.hasDarkManDebt ? ' has-dark-man-debt' : ''}`}>
              {variant !== 'reference' && investigator.hasDarkManDebt && <DarkManDebtBadge />}
              {statusChips.map(status => (
                <span key={status} className={`status-chip status-${status.toLowerCase()}`}>
                  {status}
                </span>
              ))}
            </div>
          )}
          {investigator.statistics && showStats && (
            <div className="statistics-hint">
              <button
                type="button"
                className={`tooltip-button statistics-hint-button${showStatisticsPopup ? ' active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStatisticsPopup(!showStatisticsPopup);
                }}
                aria-expanded={showStatisticsPopup}
                aria-label="Show investigator statistics"
                title="Show investigator statistics"
              >
                ?
              </button>
              {showStatisticsPopup && (
                <div
                  className="statistics-popup"
                  role="dialog"
                  aria-label={`${investigator.name} statistics`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <StatisticsCard investigator={investigator} />
                </div>
              )}
            </div>
          )}
          <div className="investigator-overlay top-right">
            <div className="stat-icons" ref={statIconsRef}>
              <div className="stat-row brains with-tooltip">
                <button
                  type="button"
                  className={`tooltip-button${openStatTooltip?.key === 'sanity' ? ' active' : ''}`}
                  onClick={(e) => investigator.maxSanityBreakdown && toggleStatTooltip('sanity', investigator.maxSanityBreakdown, e)}
                  aria-expanded={openStatTooltip?.key === 'sanity'}
                  style={{
                    visibility: investigator.maxSanityBreakdown && investigator.maxSanityBreakdown.modifiers && investigator.maxSanityBreakdown.modifiers.length > 0 ? 'visible' : 'hidden'
                  }}
                  title="Show sanity modifiers"
                >
                  ?
                </button>
                {buildHearts(investigator.sanity, investigator.maxSanity, investigator.sanityAboveMaximum, 'brain')}
                {openStatTooltip?.key === 'sanity' && investigator.maxSanityBreakdown && (
                  <StatTooltip
                    breakdown={investigator.maxSanityBreakdown}
                    setOpenStatTooltip={setOpenStatTooltip}
                    type="Sanity"
                    styles={{
                      left: `${openStatTooltip.left + 10}px`, top: `${openStatTooltip.top}px`
                    }}
                  />
                )}
              </div>
              <div className="stat-row hearts with-tooltip">
                <button
                  type="button"
                  className={`tooltip-button${openStatTooltip?.key === 'stamina' ? ' active' : ''}`}
                  onClick={(e) => investigator.maxStaminaBreakdown && toggleStatTooltip('stamina', investigator.maxStaminaBreakdown, e)}
                  aria-expanded={openStatTooltip?.key === 'stamina'}
                  style={{
                    visibility: investigator.maxStaminaBreakdown && investigator.maxStaminaBreakdown.modifiers && investigator.maxStaminaBreakdown.modifiers.length > 0 ? 'visible' : 'hidden'
                  }}
                  title="Show stamina modifiers"
                >
                  ?
                </button>
                {buildHearts(investigator.stamina, investigator.maxStamina, investigator.staminaAboveMaximum, 'heart')}
                {openStatTooltip?.key === 'stamina' && investigator.maxStaminaBreakdown && (
                  <StatTooltip
                    breakdown={investigator.maxStaminaBreakdown}
                    setOpenStatTooltip={setOpenStatTooltip}
                    type="Stamina"
                    styles={{
                      left: `${openStatTooltip.left + 10}px`, top: `${openStatTooltip.top}px`
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          <div className={`slider-overlay ${investigator.name}`} ref={sliderOverlayRef}>
            {!!setActions.length && onExecuteAction && (
              <div className="slider-adjust-controls">
                {[
                  { left: 'Speed' },
                  { left: 'Fight' },
                  { left: 'Lore' },
                  { left: 'Max_Sanity' },
                ].map((pair, idx) => {
                  const moveSkillActions = setActions.filter(a => a.id.startsWith(`set_${pair.left.toLowerCase()}`));
                  if (!moveSkillActions || !moveSkillActions.length) {
                    if (idx === 3) {
                      return null;
                    }

                    return <div key={pair.left} className="slider-adjust-row" />;
                  }
                  const baseMargin = idx % 2 === 1 ? 39.5 : 29.5;
                  return (
                    <div key={pair.left} className="slider-adjust-row">
                      {moveSkillActions.map((action) => {
                        const position = action.sliderPosition;
                        if (position == null) return null;
                        return (
                          <img
                            className='slider-set-handle-img'
                            data-game-action={action.disabled ? undefined : action.id}
                            src={sliderImage}
                            onClick={
                              (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                if (!action.disabled) {
                                  onExecuteAction(action.id);
                                }
                              }
                            }
                            style={{
                              marginLeft: `${baseMargin - 8 + (position) * 20}%`,
                              opacity: action.disabled ? '.1' : '.5',
                              cursor: action.disabled ? 'not-allowed' : 'pointer'
                            }}
                            alt="Slider"
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
            <div className='slider-handle'>
              <img loading="lazy"
                src={sliderImage}
                alt="Skill slider"
                className={"slider-handle-img slider-handle-img-0"}
                style={{ marginLeft: `${20 + (investigator.speed - investigator.speedMin) * 20}%` }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <div className='slider-handle'>
              <img loading="lazy"
                src={sliderImage}
                alt="Skill slider"
                className={"slider-handle-img slider-handle-img-1"}
                style={{ marginLeft: `${30 + (investigator.fight - investigator.fightMin) * 20}%` }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            <div className='slider-handle'>
              <img loading="lazy"
                src={sliderImage}
                alt="Skill slider"
                className={"slider-handle-img slider-handle-img-2"}
                style={{ marginLeft: `${20 + (investigator.lore - investigator.loreMin) * 20}%` }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
            {investigator.name === 'Lily Chen' && (
              <div className='slider-handle'>
                <img loading="lazy"
                  src={sliderImage}
                  alt="Skill slider"
                  className={"slider-handle-img slider-handle-img-2"}
                  style={{ marginLeft: `${30 + ((investigator.baseMaxSanity || 0) - 4) * 20}%` }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            )}
          </div>
        </div>
        {(deckInfo?.commonItems?.bottomCard || deckInfo?.uniqueItems?.bottomCard || deckInfo?.spells?.bottomCard) && (
          <div className="bottom-cards-tooltip-wrapper">
            <button
              type="button"
              className={`tooltip-button${openBottomCardTooltip ? ' active' : ''}`}
              onClick={() => setOpenBottomCardTooltip(!openBottomCardTooltip)}
              aria-expanded={openBottomCardTooltip}
              title="Show revealed bottom cards"
            >
              ?
            </button>
            {openBottomCardTooltip && (
              <div className="skill-tooltip bottom-cards-tooltip" onClick={() => setOpenBottomCardTooltip(false)}>
                <div className="bottom-cards-grid">
                  {deckInfo?.commonItems?.bottomCard && <Card card={{
                    item: deckInfo?.commonItems?.bottomCard,
                    kind: 'common',
                  }} />}
                  {deckInfo?.uniqueItems?.bottomCard && <Card card={{
                    item: deckInfo?.uniqueItems?.bottomCard,
                    kind: 'unique',
                  }} />}
                  {deckInfo?.spells?.bottomCard && <Card card={{
                    item: deckInfo?.spells?.bottomCard,
                    kind: 'spell',
                  }} />}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="skills-and-items">
          <div className="stacked-items">
            <StackedItems title="Personal" items={[]} kind="personalStory" entries={[
              ...(onExecuteAction && investigator.personalStory ? [{ item: investigator.personalStory, kind: 'personalStory' as const }] : []),
              ...[investigator.relationship, investigator.partnerRelationship].filter((card): card is NonNullable<typeof card> => !!card)
                .map(item => ({ item, kind: 'relationship' as const })),
            ]} />
            <StackedItems title="Common" items={investigator.commonItems || []} kind="common" investigatorName={investigator.name} />
            <StackedItems title="Unique" items={investigator.uniqueItems || []} kind="unique" investigatorName={investigator.name} />
            <StackedItems title="Spells" items={investigator.spells || []} kind="spell" investigatorName={investigator.name} />
            <StackedItems title="Skills" items={investigator.skills || []} kind="skill" />
            <StackedItems title="Allies" items={investigator.allies || []} kind="ally" />
            <StackedItems title="Special" items={investigator.specialCards || []} kind="special" />
            <StackedItems title="Traumas" items={[]} kind="injury" entries={[
              ...(investigator.traumas || []).map(item => ({ item, kind: 'injury' as const })),
              ...(investigator.madnesses || []).map(item => ({ item, kind: 'madness' as const })),
            ]} />
            <StackedItems title="Corruptions" items={investigator.corruptions || []} kind="corruption" />
          </div>
        </div>
      </div>

      {!onExecuteAction && investigator.personalStory && (
        <div className='investigator-personal-stories-wrapper'>
          <PersonalStoryCard card={investigator.personalStory} hideTooltip />
          <div className='investigator-personal-stories'>
            {!!investigator.personalStory?.rewardDescription && !!investigator.personalStory?.rewardName && (
              <PersonalStoryCard card={{
                name: investigator.personalStory?.rewardName,
                isReward: true,
                description: investigator.personalStory?.rewardDescription
              }} />
            ) }
            {!!investigator.personalStory?.failDescription && !!investigator.personalStory?.failName && (
              <PersonalStoryCard card={{
                name: investigator.personalStory?.failName,
                isReward: false,
                description: investigator.personalStory?.failDescription
              }} />
            ) }
          </div>
        </div>
      )}

      {(!!investigator.monsterTrophies?.length || !!investigator.gateTrophies?.length) && (
        <div className="trophies-inline">
          {investigator.gateTrophies?.map((gate, idx) => (
            <GateMarker
              key={`${gate.otherWorld}-${idx}`}
              gate={gate}
              onClickStopPropagation
              language={language}
            />
          ))}
          <MonstersDetails monsters={investigator.monsterTrophies || []} />
        </div>
      )}
    </div>
  );
};

type InvestigatorImageProps = {
  investigator: Investigator;
}
export const InvestigatorImage = ({investigator}: InvestigatorImageProps) => {
  const focusBlockRef = useRef<HTMLDivElement>(null);
  const [openSkillTooltip, setOpenSkillTooltip] = useState<TooltipPosition | null>(null);
  const toggleSkillTooltip = (key: string, data: SkillBreakdown, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (openSkillTooltip?.key === key) {
      setOpenSkillTooltip(null);
      return;
    }

    const focusBlockRect = focusBlockRef.current?.getBoundingClientRect();
    const btnRect = event.currentTarget.getBoundingClientRect();

    if (focusBlockRect) {
      setOpenSkillTooltip({
        key,
        left: btnRect.right - focusBlockRect.left,
        bottom: btnRect.bottom - focusBlockRect.bottom,
        data,
      });
    } else {
      setOpenSkillTooltip({ key, left: 0, bottom: 0, data: null });
    }
  };

  const SkillRow = ({breakDown, skill, max, color = 'blue', offset = false}: {offset?: boolean, color?: 'blue' | 'red', breakDown: SkillBreakdown | undefined, skill: string, max: number}) => {
    const skillLower = skill.toLocaleLowerCase();
    const skillName = skill.startsWith('Max') ? skill.replace ('Max', 'Max ') : skill;
    return (
      <div className={`investigator-${skillLower}`}>
        {!!breakDown && (
          <div className="skill-tooltip-icon-wrapper">
            <button
              type="button"
              className={`tooltip-button${openSkillTooltip?.key === skill ? ' active' : ''}`}
              onClick={(e) => breakDown && toggleSkillTooltip(skill, breakDown, e)}
              style={{
                visibility: breakDown.modifiers && breakDown.modifiers.length > 0 ? 'visible' : 'hidden'
              }}
              aria-expanded={openSkillTooltip?.key === skill}
              title={`Show ${skill} modifiers`}
            >
              ?
            </button>
          </div>
        )}
        <div className='investigator-label'>
          {skillName}{getBonus(breakDown)}
        </div>
        {color === 'blue' ? <SkillBlue offset={offset} maxValue={max} /> : <SkillRed offset={offset} maxValue={max} ascending={!!investigator.skillSlidersIncreaseTogether && !skill.startsWith('Max')} />}
      </div>
    );
  }

  const portraitPath = `/Investigators/tokens/${encodeURIComponent(investigator.name)}.jpg`;
  const overlayPath = "/Investigators/Investigator.png";
  return (
    <div className={`investigator-text ${investigator.name}`}>
      <img loading="lazy"
        src={portraitPath}
        alt={investigator.name}
        className='investigator-portrait-face'
      />
      <img loading="lazy"
        src={overlayPath}
        alt="investigator overlay"
        className='investigator-portrait-overlay'
      />
      <div className='investigator-name'>
        {investigator.name}
      </div>
      <div className='investigator-sanity'>
        {investigator.baseMaxSanity} {getBonus(investigator.maxSanityBreakdown)} Sanity
      </div>
      <div className='investigator-stamina'>
        {investigator.baseMaxStamina} {getBonus(investigator.maxStaminaBreakdown)} Stamina
      </div>
      <div className='investigator-home'>
        <div className='investigator-label'>
          Home:
        </div>
        <div className='investigator-home-location'>
          {investigator.startingLocation}
        </div>
      </div>
      <div className='investigator-fixed-posessions'>
        <div className='investigator-label'>
          Fixed Posessions:
        </div>
        <div className='investigator-home-location'>
          {investigator.fixedPossessionsDescription}
        </div>
      </div>
      <div className='investigator-random-posessions'>
        <div className='investigator-label'>
          Random Posessions:
        </div>
        <div className='investigator-home-location'>
          {investigator.randomPossessionsDescription}
        </div>
      </div>
      <div className='investigator-focus' ref={focusBlockRef}>
        <div className='investigator-label'>
          Focus:
        </div>
        <div className='investigator-value'>
          {investigator.focus}
        </div>
      </div>
      <div className='investigator-ability'>
        <div className='investigator-home-location'>
          <SimpleMarkdown
            text={investigator.abilityDescription || ''}
            keyPrefix={`${investigator.name}-ability-description`}
          />
        </div>
      </div>
      {!!openSkillTooltip && (
        <StatTooltip
          breakdown={openSkillTooltip.data}
          setOpenStatTooltip={setOpenSkillTooltip}
          type={openSkillTooltip.key}
          styles={{
            left: `${openSkillTooltip.left}px`,
            bottom: `${openSkillTooltip.bottom}px`
          }}
        />
      )}
      <SkillRow breakDown={investigator.speedBreakdown} max={investigator.speedMax} skill='Speed' />
      <SkillRow breakDown={investigator.sneakBreakdown} max={investigator.sneakMax} skill='Sneak' color='red' />
      <SkillRow breakDown={investigator.fightBreakdown} offset max={investigator.fightMax} skill='Fight' />
      <SkillRow breakDown={investigator.willBreakdown} color='red' offset max={investigator.willMax} skill='Will' />
      <SkillRow breakDown={investigator.loreBreakdown} max={investigator.loreMax} skill='Lore' />
      <SkillRow breakDown={investigator.luckBreakdown} max={investigator.luckMax} skill='Luck' color='red' />
      {investigator.name === 'Lily Chen' && (
        <SkillRow offset breakDown={investigator.maxSanityBreakdown} max={7} skill='MaxSanity' />
      )}
      {investigator.name === 'Lily Chen' && (
        <SkillRow offset breakDown={investigator.maxStaminaBreakdown} max={7} skill='MaxStamina' color='red' />
      )}
    </div>
  )
}

const getBonus = (breakdown?: SkillBreakdown) => {
  if (!breakdown) {
    return null;
  }
  const diff = breakdown.total - breakdown.base;
  if (diff === 0) {
    return null;
  }

  return <div className='bonus' style={{ color: getStatColor(breakdown) }}>{diff > 0 ? '+' : ''}{diff}</div>;
}

type SkillBlueProps = {
  maxValue: number,
  offset?: boolean
}
const SkillBlue = ({maxValue, offset = false}: SkillBlueProps) => {
  return (
    <div className={`skill-1 ${offset ? 'offset' : ''}`}>
      <div className='investigator-label skill-blue'>
        {getValue(maxValue - 3)}
      </div>
      <div className='investigator-label skill-blue'>
        {getValue(maxValue - 2)}
      </div>
      <div className='investigator-label skill-blue'>
        {getValue(maxValue - 1)}
      </div>
      <div className='investigator-label skill-blue'>
        {getValue(maxValue)}
      </div>
    </div>
  );
}

type SkillRedProps = {
  maxValue: number,
  offset?: boolean,
  ascending?: boolean
}
const SkillRed = ({maxValue, offset = false, ascending = false}: SkillRedProps) => {
  return (
    <div className={`skill-1 ${offset ? 'offset' : ''}`}>
      <div className='investigator-label skill-red'>
        {getValue(ascending ? maxValue - 3 : maxValue)}
      </div>
      <div className='investigator-label skill-red'>
        {getValue(ascending ? maxValue - 2 : maxValue - 1)}
      </div>
      <div className='investigator-label skill-red'>
        {getValue(ascending ? maxValue - 1 : maxValue - 2)}
      </div>
      <div className='investigator-label skill-red'>
        {getValue(ascending ? maxValue : maxValue - 3)}
      </div>
    </div>
  );
}

const getValue = (value: number) => Math.max(value, 0);
